import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  useRfqBuilderActions,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';

import { selectActiveProducts } from '@paradigm/store/auth';
import { useTrackAction } from '@paradigm/features/src/analytics';
import Badge from '@paradigm/design-system/src/components/Badge';
import { isProductCodeDisabledForType } from '@paradigm/features/src/products/domain';
import { PRODUCTS } from '@paradigm/features/src/products/config';
import ToggleButton from './components/ToggleButton';

export default function ProductButtons() {
  const activeProducts = useSelector(selectActiveProducts);
  const { productShortcut, auction } = useRfqBuilderView();
  const { options: allOptions, selectedOption } = productShortcut;
  const options = allOptions.filter((opt) => {
    const product = PRODUCTS[opt.value];
    return product != null && activeProducts.includes(product.venue);
  });
  const { selectProduct } = useRfqBuilderActions();
  const trackAction = useTrackAction();

  const handleOnChange = useCallback(
    (productCode: string) => {
      return () => {
        trackAction('update product', { productCode });
        selectProduct(productCode);
      };
    },
    [selectProduct, trackAction],
  );

  return useMemo(
    () => (
      <>
        {options.map((option) => {
          return (
            <ToggleButton
              ariaLabel={`select ${option.label}`}
              key={option.value}
              active={option.value === selectedOption?.value}
              onClick={handleOnChange(option.value)}
              disabled={isProductCodeDisabledForType(
                option.value,
                auction.selectedOption.value,
              )}
            >
              {PRODUCTS[option.value]?.isNew && (
                <Badge variant="new" top={-4} right={-8}>
                  new
                </Badge>
              )}
              {option.label}
            </ToggleButton>
          );
        })}
      </>
    ),
    [
      auction.selectedOption.value,
      handleOnChange,
      options,
      selectedOption?.value,
    ],
  );
}
